import { type TypedDocumentNode, gql } from '@apollo/client';
import { faStars } from '@fortawesome/pro-solid-svg-icons';
import styled, { css } from 'styled-components';

import {
  InGameCurrency,
  Rarity,
} from '@sorare/core/src/__generated__/globalTypes';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Energy } from '@sorare/core/src/atoms/icons/Energy';
import { Fiat } from '@sorare/core/src/atoms/icons/Fiat';
import { NewScarcityIcon } from '@sorare/core/src/atoms/icons/ScarcityIcon';
import { FRONTEND_ASSET_HOST } from '@sorare/core/src/constants/assets';
import { shardsIconUrl } from '@sorare/core/src/lib/rewards';
import { toSnakeCase } from '@sorare/core/src/lib/toSnakeCase';

import type { AnyRewardConfigImage_anyRewardConfigInterface } from './__generated__/index.graphql';

const StyledImg = styled.img`
  width: 100%;
  aspect-ratio: 1 / 1;
  object-fit: contain;
`;
const forcedComponentStyle = css`
  width: 90% !important;
  height: unset !important;
  aspect-ratio: 1/1;
`;
const StyledFiat = styled(Fiat)`
  ${forcedComponentStyle}
`;
const StyledCard = styled(NewScarcityIcon)`
  ${forcedComponentStyle}
`;

type Props = {
  anyRewardConfig: AnyRewardConfigImage_anyRewardConfigInterface;
  rarity?: Rarity;
};

const getRewardImage = (
  anyRewardConfig: AnyRewardConfigImage_anyRewardConfigInterface,
  rarity?: Rarity
) => {
  switch (anyRewardConfig.__typename) {
    case 'CoinRewardConfig':
      return 'coin';
    case 'InGameCurrencyRewardConfig':
      switch (anyRewardConfig.currency) {
        case InGameCurrency.OLD_CLUB_SHOP_COIN:
          return 'coin';
        case InGameCurrency.LIMITED_XP:
          return 'xp-limited';
        case InGameCurrency.RARE_XP:
          return 'xp-rare';
        case InGameCurrency.SUPER_RARE_XP:
          return 'xp-super_rare';
        case InGameCurrency.UNIQUE_XP:
          return 'xp-unique';
        default:
          return '';
      }
    case 'ConversionCreditRewardConfig':
      return 'conversion-credit';
    case 'ProbabilisticBundleRewardConfig':
      return `box-${toSnakeCase(rarity || Rarity.common)}`;
    case 'ShopItemRewardConfig':
      return `level-up-${toSnakeCase(rarity || Rarity.limited)}`;
    case 'DeliverableItemRewardConfig':
      return {
        ExperienceDeliverableItem: '',
        JerseyDeliverableItem: 'jersey',
        TicketsDeliverableItem: 'tickets',
        WearableDeliverableItem: 'merch',
      }[anyRewardConfig.deliverableItem.__typename];
    default:
      return '';
  }
};

const REWARDS_ASSETS_URL = `${FRONTEND_ASSET_HOST}/football/rewards`;

export const AnyRewardConfigImage = ({ anyRewardConfig, rarity }: Props) => {
  switch (anyRewardConfig.__typename) {
    case 'MonetaryRewardConfig':
      return <StyledFiat />;
    case 'CardRewardConfig':
      return <StyledCard rarity={rarity || Rarity.common} />;
    case 'CardShardRewardConfig':
      return <StyledImg src={shardsIconUrl(rarity || Rarity.common)} alt="" />;
    case 'ShopItemRewardConfig':
      return <StyledImg src={anyRewardConfig.shopItem.pictureUrl} alt="" />;
    case 'ExperienceRewardConfig':
      return anyRewardConfig.pictureUrl ? (
        <StyledImg src={anyRewardConfig.pictureUrl} alt="" />
      ) : (
        <FontAwesomeIcon icon={faStars} />
      );
    case 'InGameCurrencyRewardConfig':
      switch (anyRewardConfig.currency) {
        case InGameCurrency.LIMITED_ENERGY:
          return <Energy variant={Rarity.limited} />;
        case InGameCurrency.RARE_ENERGY:
          return <Energy variant={Rarity.rare} />;
        case InGameCurrency.SUPER_RARE_ENERGY:
          return <Energy variant={Rarity.super_rare} />;
        case InGameCurrency.UNIQUE_ENERGY:
          return <Energy variant={Rarity.unique} />;
        default:
          return (
            <StyledImg
              src={`${REWARDS_ASSETS_URL}/icons/${getRewardImage(anyRewardConfig, rarity)}.png`}
              alt=""
            />
          );
      }
    default:
      return (
        <StyledImg
          src={`${REWARDS_ASSETS_URL}/icons/${getRewardImage(anyRewardConfig, rarity)}.png`}
          alt=""
        />
      );
  }
};

AnyRewardConfigImage.fragments = {
  anyRewardConfigInterface: gql`
    fragment AnyRewardConfigImage_anyRewardConfigInterface on AnyRewardConfigInterface {
      id
      ... on InGameCurrencyRewardConfig {
        id
        currency
      }
      ... on CardRewardConfig {
        id
        rarity
      }
      ... on CardShardRewardConfig {
        id
        rarity
      }
      ... on ShopItemRewardConfig {
        id
        shopItem {
          id
          position
          pictureUrl
        }
      }
      ... on DeliverableItemRewardConfig {
        id
        deliverableItem {
          slug
        }
      }
      ... on ExperienceRewardConfig {
        id
        pictureUrl
      }
    }
  ` as TypedDocumentNode<AnyRewardConfigImage_anyRewardConfigInterface>,
};
